import React, { Fragment } from 'react'
import { type CrossClientQueryDataSource, type CrossClientQueryExecution, getExecutionStartedAt } from '../../../../models/CrossClient'
import { CrossClientResultSourceIcon } from '../Sources/CrossClientResultSourceIcon'
import { ReactComponent as ZocksIcon } from '../../../../assets/images/ZocksIcon-primary.svg'
import { CLIENT_MODE_LABEL, isZocksSource } from '../../utils'
import { formatRelativeDateTimeShort } from '../../../../common/dateUtils'

interface CrossClientResultsHeaderProps {
  execution: CrossClientQueryExecution
}

export const CrossClientResultsHeader: React.FC<CrossClientResultsHeaderProps> = ({ execution }) => {
  const startedLabel = formatRelativeDateTimeShort(new Date(getExecutionStartedAt(execution)))

  const tags: { key: string; content: React.ReactNode }[] = []
  if (execution.clientQueryMode) {
    tags.push({
      key: 'clients',
      content: (
        <>
          <span className='paragraph-small medium zocks-text-gray-600'>Clients:</span>
          <span className='paragraph-small zocks-text-gray-600'>{CLIENT_MODE_LABEL[execution.clientQueryMode]}</span>
        </>
      ),
    })
  }
  if (execution.sources.length > 0) {
    tags.push({
      key: 'sources',
      content: (
        <>
          <span className='paragraph-small medium zocks-text-gray-600'>Sources:</span>
          <SourceIcons sources={execution.sources} />
        </>
      ),
    })
  }
  const startedLabelKey = execution.bulkStartedAt ? 'Started' : 'Queued'
  tags.push({
    key: 'started',
    content: (
      <>
        <span className='paragraph-small medium zocks-text-gray-600'>{startedLabelKey}</span>
        <span className='paragraph-small zocks-text-gray-600'>{startedLabel}</span>
      </>
    ),
  })

  return (
    <div className='d-flex flex-column gap-1'>
      <h4 className='mb-0'>{execution.userQuestion}</h4>
      <div className='d-flex flex-wrap gap-2 align-items-center'>
        {tags.map(({ key, content }, idx) => (
          <Fragment key={key}>
            {idx > 0 ? <span className='paragraph-small zocks-text-gray-600'>•</span> : null}
            <div className='d-flex gap-1 align-items-center'>{content}</div>
          </Fragment>
        ))}
      </div>
    </div>
  )
}

interface SourceIconsProps {
  sources: CrossClientQueryDataSource[]
}

const SourceIcons: React.FC<SourceIconsProps> = ({ sources }) => {
  const hasZocks = sources.some(isZocksSource)
  const integrationSources: { integrationType: string }[] = []
  const seenIntegrations = new Set<string>()
  for (const source of sources) {
    if (source.type !== 'integration' || seenIntegrations.has(source.integrationType)) {
      continue
    }
    seenIntegrations.add(source.integrationType)
    integrationSources.push({ integrationType: source.integrationType })
  }

  return (
    <div className='d-flex gap-1 align-items-center'>
      {hasZocks ? <ZocksIcon width='1rem' height='1rem' /> : null}
      {integrationSources.map(({ integrationType }) => (
        <CrossClientResultSourceIcon key={integrationType} source={{ type: 'integration', integrationType }} size='md' />
      ))}
    </div>
  )
}
